import { supabase } from '@/integrations/supabase/client';
import { getUserGymId } from '@/utils/gymAuth';
import { invokeWithRetry } from './common/invokeWithRetry';
import { isTransportFailure, isLocalWriteFallbackAllowed } from './common/isTransportFailure';
import { SessionExpiredError } from './common/sessionExpired';

/**
 * Rappel sur `subscriptions-management` (cf. `supabase/functions/subscriptions-management/index.ts`) :
 * la fonction renvoie **400 pour tout**, y compris ses gardes métier
 * (`sessions_total >= 1`, remplacement des abonnements recoupant le planning) et
 * le refus d'accès à la salle. Le critère « tout statut < 500 est une décision du
 * serveur » d'`isTransportFailure` couvre donc bien ce cas : un 400 n'autorise
 * plus le repli en écriture directe.
 *
 * Les LECTURES utilisent `isTransportFailure`, les ÉCRITURES
 * `isLocalWriteFallbackAllowed`, qui refuse en plus le repli sur un dépassement
 * du délai client : la fonction edge peut alors être encore en train d'expirer
 * les recoupants puis d'insérer, et rejouer la même séquence ici (elle ne lit
 * que les `status = 'active'`, jamais un `pending` ni une insertion serveur pas
 * encore committée) risquerait une double expiration / un doublon d'actifs.
 */

// ✅ Feature flags for progressive activation
const FEATURE_FLAGS = {
  USE_EDGE_FUNCTION_GET_USER_SUBSCRIPTIONS: true,
  USE_EDGE_FUNCTION_PAUSE_SUBSCRIPTION: true,
  USE_EDGE_FUNCTION_RESUME_SUBSCRIPTION: true,
  USE_EDGE_FUNCTION_GET_SUBSCRIPTION_STATS: true,
  USE_EDGE_FUNCTION_CREATE_SUBSCRIPTION: true,
  USE_EDGE_FUNCTION_UPDATE_SUBSCRIPTION: true,
};

/**
 * Wrapper for subscriptions-management edge function: get_user_subscriptions action
 * Centralizes all user subscriptions fetching with gym_id validation
 */
export async function getUserSubscriptions(
  userId?: string,
  status?: string,
  gymId?: string
): Promise<any[]> {
  const targetGymId = gymId || getUserGymId();
  
  if (!targetGymId) {
    throw new Error('gym_id is required for getUserSubscriptions');
  }

  if (FEATURE_FLAGS.USE_EDGE_FUNCTION_GET_USER_SUBSCRIPTIONS) {
    console.log('[subscriptionsWrapper] Using edge function for getUserSubscriptions');
    const { data, error, authExpired, forbidden } = await invokeWithRetry('subscriptions-management', {
      action: 'get_user_subscriptions',
      gym_id: targetGymId,
      user_id: userId,
      status,
    });

    if (authExpired) {
      // Surtout pas `return []` : le quota de séances se déduit de cette
      // liste, et une liste vide y signifie « aucun abonnement actif », donc
      // « 0 séance restante », donc « limite atteinte » affiché à un adhérent
      // qui a peut-être un abonnement plein. On lève une erreur reconnaissable
      // (cf. sessionExpired.ts) : les appelants la distinguent d'un échec
      // ordinaire et restent muets, la redirection vers /login étant déjà
      // lancée par invokeWithRetry.
      throw new SessionExpiredError('subscriptions-management/get_user_subscriptions');
    }

    if (error) {
      // Le repli en lecture directe n'est légitime que si la fonction n'a pas
      // répondu. Sur un refus (403 droits, 400 « does not have access to this
      // gym »), rejouer la requête depuis le navigateur revient à contourner la
      // décision du serveur : on propage l'erreur à l'appelant.
      if (!isTransportFailure(error, { authExpired, forbidden })) {
        console.error('[subscriptionsWrapper] getUserSubscriptions refused by server, no fallback:', error);
        throw error;
      }
      console.error('[subscriptionsWrapper] Edge function unreachable, falling back to direct query:', error);
    } else {
      return data?.subscriptions || [];
    }
  }

  // Fallback: direct database query
  console.log('[subscriptionsWrapper] Using direct query for getUserSubscriptions (fallback)');
  
  const currentUser = (await supabase.auth.getUser()).data.user;
  const targetUserId = userId || currentUser?.id;

  if (!targetUserId) {
    throw new Error('User ID is required');
  }

  let query = supabase
    .from('subscriptions')
    .select('*')
    .eq('user_id', targetUserId)
    .eq('gym_id', targetGymId)
    .order('created_at', { ascending: false });

  if (status) {
    query = query.eq('status', status);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data || [];
}

/**
 * Wrapper for subscriptions-management edge function: get_user_subscriptions_bulk action
 * Fetches subscriptions for multiple users in a single query (performance optimization)
 */
export async function getUserSubscriptionsBulk(
  userIds: string[], 
  gymId?: string
): Promise<any[]> {
  const targetGymId = gymId || getUserGymId();
  
  if (!targetGymId) {
    throw new Error('gym_id is required for getUserSubscriptionsBulk');
  }

  if (!userIds || userIds.length === 0) {
    return [];
  }

  if (FEATURE_FLAGS.USE_EDGE_FUNCTION_GET_USER_SUBSCRIPTIONS) {
    console.log('[subscriptionsWrapper] Using edge function for getUserSubscriptionsBulk');
    const { data, error, authExpired, forbidden } = await invokeWithRetry('subscriptions-management', {
      action: 'get_user_subscriptions_bulk',
      user_ids: userIds,
      gym_id: targetGymId,
    });

    if (authExpired) {
      // Même raisonnement qu'au singulier : une liste vide se lit « aucun de
      // ces adhérents n'a d'abonnement », ce qui affiche une liste de clients
      // tous marqués sans abonnement au lieu de signaler l'expiration.
      throw new SessionExpiredError('subscriptions-management/get_user_subscriptions_bulk');
    }

    if (error) {
      // Même règle que ci-dessus : un refus du serveur ne se rejoue pas en
      // direct depuis le navigateur.
      if (!isTransportFailure(error, { authExpired, forbidden })) {
        console.error('[subscriptionsWrapper] getUserSubscriptionsBulk refused by server, no fallback:', error);
        throw error;
      }
      console.error('[subscriptionsWrapper] Edge function unreachable, falling back to direct query:', error);
    } else {
      return data?.subscriptions || [];
    }
  }

  // Fallback: direct database query
  console.log('[subscriptionsWrapper] Using direct query for getUserSubscriptionsBulk (fallback)');
  const { data, error } = await supabase
    .from('subscriptions')
    .select('*')
    .in('user_id', userIds)
    .eq('gym_id', targetGymId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[subscriptionsWrapper] Fallback failed:', error);
    throw error;
  }

  return data || [];
}

/**
 * Wrapper for subscriptions-management edge function: pause_subscription action
 * Centralizes subscription pausing with permission validation
 */
export async function pauseSubscription(
  subscriptionId: string,
  breakStart: string,
  gymId?: string
): Promise<boolean> {
  const targetGymId = gymId || getUserGymId();
  
  if (!targetGymId) {
    throw new Error('gym_id is required for pauseSubscription');
  }

  if (FEATURE_FLAGS.USE_EDGE_FUNCTION_PAUSE_SUBSCRIPTION) {
    console.log('[subscriptionsWrapper] Using edge function for pauseSubscription');
    const { data, error, authExpired, forbidden } = await invokeWithRetry('subscriptions-management', {
      action: 'pause_subscription',
      gym_id: targetGymId,
      subscription_id: subscriptionId,
      break_start: breakStart,
    });

    if (authExpired) {
      throw new SessionExpiredError('subscriptions-management/pause_subscription');
    }

    if (error) {
      // Le repli RPC exécute la même mutation en court-circuitant les contrôles
      // de la fonction edge : on ne l'autorise que si la fonction n'a jamais
      // répondu.
      if (!isLocalWriteFallbackAllowed(error, { authExpired, forbidden })) {
        console.error('[subscriptionsWrapper] pauseSubscription refused by server, no RPC fallback:', error);
        throw error;
      }
      console.error('[subscriptionsWrapper] Edge function unreachable, falling back to RPC:', error);
    } else {
      return data?.success;
    }
  }

  // Fallback: direct RPC call
  console.log('[subscriptionsWrapper] Using RPC for pauseSubscription (fallback)');
  const { data, error } = await supabase.rpc('pause_subscription', {
    p_subscription_id: subscriptionId,
    p_gym_id: targetGymId,
    p_break_start: breakStart
  });

  if (error) throw error;
  return data;
}

/**
 * Wrapper for subscriptions-management edge function: resume_subscription action
 * Centralizes subscription resumption with permission validation
 */
export async function resumeSubscription(
  subscriptionId: string,
  gymId?: string
): Promise<boolean> {
  const targetGymId = gymId || getUserGymId();
  
  if (!targetGymId) {
    throw new Error('gym_id is required for resumeSubscription');
  }

  if (FEATURE_FLAGS.USE_EDGE_FUNCTION_RESUME_SUBSCRIPTION) {
    console.log('[subscriptionsWrapper] Using edge function for resumeSubscription');
    const { data, error, authExpired, forbidden } = await invokeWithRetry('subscriptions-management', {
      action: 'resume_subscription',
      gym_id: targetGymId,
      subscription_id: subscriptionId,
    });

    if (authExpired) {
      throw new SessionExpiredError('subscriptions-management/resume_subscription');
    }

    if (error) {
      // Idem pause_subscription : pas de repli RPC sur un refus du serveur.
      if (!isLocalWriteFallbackAllowed(error, { authExpired, forbidden })) {
        console.error('[subscriptionsWrapper] resumeSubscription refused by server, no RPC fallback:', error);
        throw error;
      }
      console.error('[subscriptionsWrapper] Edge function unreachable, falling back to RPC:', error);
    } else {
      return data?.success;
    }
  }

  // Fallback: direct RPC call
  console.log('[subscriptionsWrapper] Using RPC for resumeSubscription (fallback)');
  const { data, error } = await supabase.rpc('resume_subscription', {
    p_subscription_id: subscriptionId,
    p_gym_id: targetGymId
  });

  if (error) throw error;
  return data;
}

/**
 * Wrapper for subscriptions-management edge function: get_subscription_stats action
 * Centralizes subscription statistics fetching
 */
export async function getSubscriptionStats(gymId?: string): Promise<any[]> {
  const targetGymId = gymId || getUserGymId();
  
  if (!targetGymId) {
    throw new Error('gym_id is required for getSubscriptionStats');
  }

  if (FEATURE_FLAGS.USE_EDGE_FUNCTION_GET_SUBSCRIPTION_STATS) {
    console.log('[subscriptionsWrapper] Using edge function for getSubscriptionStats');
    const { data, error, authExpired, forbidden } = await invokeWithRetry('subscriptions-management', {
      action: 'get_subscription_stats',
      gym_id: targetGymId,
    });

    if (authExpired) {
      return [];
    }

    if (error) {
      // Un refus d'accès à la salle ne doit pas être contourné par un appel RPC
      // direct : l'erreur remonte.
      if (!isTransportFailure(error, { authExpired, forbidden })) {
        console.error('[subscriptionsWrapper] getSubscriptionStats refused by server, no RPC fallback:', error);
        throw error;
      }
      console.error('[subscriptionsWrapper] Edge function unreachable, falling back to RPC:', error);
    } else {
      return data?.stats || [];
    }
  }

  // Fallback: direct RPC call
  console.log('[subscriptionsWrapper] Using RPC for getSubscriptionStats (fallback)');
  const { data, error } = await supabase.rpc('get_subscription_status_stats', {
    p_gym_id: targetGymId
  });

  if (error) throw error;
  return data || [];
}

/**
 * Wrapper for subscriptions-management edge function: create_subscription action
 * Centralizes subscription creation with validation
 */
export async function createSubscription(subscriptionData: {
  user_id: string;
  type: string;
  price: number;
  start_date: string;
  end_date: string;
  sessions_total?: number;
  sessions_remaining?: number;
  jokers_total?: number;
  jokers_remaining?: number;
  payment_frequency?: string;
  payment_method?: string;
  status?: string;
  gym_id?: string;
  subscription_type_id?: string;
  is_manually_activated?: boolean;
}): Promise<any> {
  const targetGymId = subscriptionData.gym_id || getUserGymId();
  
  if (!targetGymId) {
    throw new Error('gym_id is required for createSubscription');
  }

  if (FEATURE_FLAGS.USE_EDGE_FUNCTION_CREATE_SUBSCRIPTION) {
    console.log('[subscriptionsWrapper] Using edge function for createSubscription');
    const { data, error, authExpired, forbidden } = await invokeWithRetry('subscriptions-management', {
      action: 'create_subscription',
      gym_id: targetGymId,
      ...subscriptionData,
    });

    if (authExpired) {
      throw new SessionExpiredError('subscriptions-management/create_subscription');
    }

    if (error) {
      // C'est LE site signalé par l'audit (0.7) : la fonction edge refuse en 400
      // « sessions_total doit être au minimum 1 » et « Un abonnement actif
      // existe déjà pour cet utilisateur ». L'ancien `catch` global rattrapait
      // ces refus et insérait quand même la ligne en direct, annulant les deux
      // gardes. Un refus remonte désormais tel quel à l'appelant, avec le
      // message du serveur (la nouvelle `invokeWithRetry` le recopie dans
      // `error.message`), donc directement affichable.
      if (!isLocalWriteFallbackAllowed(error, { authExpired, forbidden })) {
        console.error('[subscriptionsWrapper] createSubscription refused by server, no direct insert:', error);
        throw error;
      }
      console.error('[subscriptionsWrapper] Edge function unreachable, falling back to direct insert:', error);
    } else {
      return data?.subscription;
    }
  }

  // Repli : la fonction edge est injoignable (réseau, 5xx). On appelle la RPC
  // transactionnelle DIRECTEMENT (PostgREST, un service distinct de la fonction
  // edge, reste en général joignable) : on garde ainsi l'atomicité expire+insert
  // et le verrou anti-concurrence, sans rejouer une séquence non transactionnelle
  // depuis le navigateur (cf. migration 20260726150000).
  console.log('[subscriptionsWrapper] Edge unreachable, calling create_subscription_atomic RPC directly (fallback)');

  const sessionsTotal = subscriptionData.sessions_total;
  if (sessionsTotal === null || sessionsTotal === undefined || sessionsTotal < 1) {
    throw new Error(
      "sessions_total doit être au minimum 1. Veuillez sélectionner un type d'abonnement valide."
    );
  }

  const { data, error } = await supabase.rpc('create_subscription_atomic', {
    p_payload: { ...subscriptionData, gym_id: targetGymId, status: subscriptionData.status || 'active' },
  });

  if (error) throw error;
  return data;
}

/**
 * Wrapper for subscriptions-management edge function: update_subscription action
 * Centralizes subscription updates with permission validation
 */
export async function updateSubscription(
  subscriptionId: string,
  updates: Record<string, any>,
  gymId?: string
): Promise<any> {
  const targetGymId = gymId || getUserGymId();
  
  if (!targetGymId) {
    throw new Error('gym_id is required for updateSubscription');
  }

  if (FEATURE_FLAGS.USE_EDGE_FUNCTION_UPDATE_SUBSCRIPTION) {
    console.log('[subscriptionsWrapper] Using edge function for updateSubscription');
    const { data, error, authExpired, forbidden } = await invokeWithRetry('subscriptions-management', {
      action: 'update_subscription',
      gym_id: targetGymId,
      subscription_id: subscriptionId,
      updates,
    });

    if (authExpired) {
      // Même erreur reconnaissable que sur la lecture : sans cela le `catch` de
      // `BlockClientDialog` (qui appelle `getUserSubscriptions` PUIS
      // `updateSubscription`) n'était protégé que pour la lecture, et une
      // session qui expire entre les deux appels ré-affichait le toast
      // « L'opération a échoué » par-dessus la redirection vers /login.
      throw new SessionExpiredError('subscriptions-management/update_subscription');
    }

    if (error) {
      // « Subscription not found in this gym » et le refus d'accès à la salle
      // sont des décisions du serveur : pas d'update direct pour les contourner.
      if (!isLocalWriteFallbackAllowed(error, { authExpired, forbidden })) {
        console.error('[subscriptionsWrapper] updateSubscription refused by server, no direct update:', error);
        throw error;
      }
      console.error('[subscriptionsWrapper] Edge function unreachable, falling back to direct update:', error);
    } else {
      return data?.subscription;
    }
  }

  // Fallback: direct database update
  // `.eq('gym_id')` : cloisonnement multi-tenant, l'update ne doit jamais pouvoir
  // toucher l'abonnement d'une autre salle.
  // `.select()` : sans lui, un update bloqué par RLS renvoie 0 ligne et AUCUNE
  // erreur, et l'UI afficherait un succès mensonger. On vérifie donc
  // explicitement qu'au moins une ligne a été modifiée.
  console.log('[subscriptionsWrapper] Using direct update for updateSubscription (fallback)');
  const { data, error } = await supabase
    .from('subscriptions')
    .update(updates as never)
    .eq('id', subscriptionId)
    .eq('gym_id', targetGymId)
    .select();

  if (error) throw error;
  if (!data || data.length === 0) {
    throw new Error(
      `Subscription ${subscriptionId} not updated: not found in gym ${targetGymId} or blocked by row-level security`
    );
  }
  return data[0];
}
